import React from 'react';
import PropTypes from 'prop-types';
import Theme from "./Theme.js";
/** 
 * React class that renders a div with display flex
 * */
import { jsx as _jsx } from "react/jsx-runtime";
class Flex extends React.Component {
  static contextType = Theme.Context;
  static propTypes = {
    /**
     * CSS class to be applied to this component
     */
    className: PropTypes.string,
    /**
     * Theme variant name to be added to the component
     */
    variant: PropTypes.string,
    /**
     * Direction of the flex element's layout
     */
    direction: PropTypes.oneOf(['row', 'row-reverse', 'column', 'column-reverse', 'inherit', 'initial', 'revert', 'revert-layer', 'unset']),
    /**
     * CSS flex-wrap
     */
    wrap: PropTypes.oneOf(['nowrap', 'wrap', 'wrap-reverse', 'inherit', 'initial', 'revert', 'revert-layer', 'unset']),
    /**
     * If true, sets CSS display to inline-flex, otherwise flex
     */
    inline: PropTypes.bool,
    /**
     * If true, sets CSS min-height to 100%
     */
    vFill: PropTypes.bool,
    /**
     * CSS align-items
     */
    align: PropTypes.oneOf(['normal', 'stretch', 'center', 'start', 'end', 'flex-start', 'flex-end', 'baseline', 'first baseline', 'last baseline', 'safe center', 'unsafe center', 'inherit', 'initial', 'revert', 'revert-layer', 'unset']),
    /**
     * CSS align-self
     */
    alignSelf: PropTypes.oneOf(['normal', 'stretch', 'center', 'start', 'end', 'flex-start', 'flex-end', 'baseline', 'first baseline', 'last baseline', 'safe center', 'unsafe center', 'inherit', 'initial', 'revert', 'revert-layer', 'unset']),
    /**
     * CSS align-content
     */
    alignContent: PropTypes.oneOf(['center', 'start', 'end', 'flex-start', 'flex-end', 'normal', 'baseline', 'first baseline', 'last baseline', 'space-between', 'space-around', 'space-evenly', 'stretch', 'safe center', 'unsafe center', 'inherit', 'initial', 'revert', 'revert-layer', 'unset']),
    /**
     * CSS justify-content
     */
    justify: PropTypes.oneOf(['center', 'start', 'end', 'flex-start', 'flex-end', 'left', 'right', 'normal', 'space-between', 'space-around', 'space-evenly', 'stretch', 'safe center', 'unsafe center', 'inherit', 'initial', 'revert', 'revert-layer', 'unset']),
    /**
     * CSS gap
     */
    gap: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * CSS column-gap
     */
    columnGap: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * CSS row-gap
     */
    rowGap: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * HTML style property
     */
    style: PropTypes.object
  };
  static defaultProps = {
    className: null,
    variant: null,
    // children : null,
    direction: null,
    wrap: null,
    inline: null,
    align: null,
    alignSelf: null,
    alignContent: null,
    vFill: null,
    justify: null,
    gap: null,
    columnGap: null,
    rowGap: null,
    style: null
  };
  render() {
    const theme = this.context;
    // const { children } = this.props
    const {
      children,
      className,
      variant,
      direction,
      wrap,
      domRef,
      inline,
      justify,
      align,
      alignContent,
      alignSelf,
      // eslint-disable-next-line no-unused-vars
      style: pStyle,
      vFill,
      gap,
      columnGap,
      rowGap,
      layoutOptions,
      ...rest
    } = this.props;
    const style = pStyle == null ? {} : {
      ...pStyle
    };
    if (direction || wrap || inline != null || justify || align || alignContent || alignSelf || vFill || gap != null || columnGap != null || rowGap != null) {
      if (direction) {
        style.flexDirection = direction;
      }
      if (inline) {
        style.display = inline ? 'inline-flex' : 'flex';
      }
      if (wrap) {
        style.flexWrap = wrap;
      }
      if (justify) {
        style.justifyContent = justify;
      }
      if (align) {
        style.alignItems = align;
      }
      if (alignContent) {
        style.alignContent = alignContent;
      }
      if (alignSelf) {
        style.alignSelf = alignSelf;
      }
      if (vFill) {
        style.minHeight = '100%';
        // style.maxHeight = '100%' 
      }
      if (gap) {
        style.gap = gap;
      }
      if (columnGap) {
        style.columnGap = columnGap;
      }
      if (rowGap) {
        style.rowGap = rowGap;
      }
    }
    return /*#__PURE__*/_jsx("div", {
      className: theme.classes('Flex', 'flex', variant, className),
      ref: domRef,
      style: style,
      ...rest,
      children: children
    });
  }
}
class Layout extends React.Component {
  static propTypes = {
    grow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shrink: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    basis: PropTypes.string,
    flex: PropTypes.string,
    align: PropTypes.string,
    style: PropTypes.object,
    children: PropTypes.node
  };
  static defaultProps = {
    grow: null,
    shrink: null,
    basis: null,
    flex: null,
    align: null,
    style: null,
    children: null
  };
  render() {
    const {
      children,
      grow,
      shrink,
      flex,
      basis,
      align,
      style
    } = this.props;
    const c = React.Children.count(children);
    if (c === 0) {
      return null;
    }
    if (c > 1) {
      throw new Error('Flex.Layout supports only one child');
    }
    let elem = React.Children.only(children);
    const bStyle = style == null ? {} : {
      ...style
    };
    if (flex != null) {
      bStyle.flex = flex;
    } else if (grow != null) {
      bStyle.flex = `${grow} ${shrink == null ? '' : shrink} ${basis == null ? '' : basis}`.trim();
      if (align) {
        bStyle.flexAlign = align;
      }
    } else if (shrink != null || basis != null || align != null) {
      if (align) {
        bStyle.alignSelf = align;
      }
      if (shrink) {
        bStyle.flexShrink = shrink;
      }
      if (basis) {
        bStyle.flexBasis = basis;
      }
    } else {
      return elem;
    }
    let cStyle = elem.props && elem.props.style;
    cStyle = cStyle ? {
      ...cStyle,
      ...bStyle
    } : bStyle;
    elem = /*#__PURE__*/React.cloneElement(elem, {
      style: cStyle
    });
    return elem;
  }
}
Flex.Layout = Layout;
export default Flex;